import {
  createContext,
  useCallback,
  useContext,
  useState,
  type FC,
  type ReactNode,
} from "react";

export type ConnectionState = {
  status: string;
  direction?: string;
};

type StatusMap = Record<string, ConnectionState>;

type ConnectionStatusContextValue = {
  getOptimisticStatus: (userId: string) => string | null;
  getConnectionState: (userId: string) => ConnectionState | null;
  setOptimisticStatus: (
    userId: string,
    status: string | null,
    direction?: string,
  ) => void;
  syncFromServer: (
    userId: string,
    status: string,
    direction?: string | null,
    force?: boolean,
  ) => void;
};

const ConnectionStatusContext = createContext<
  ConnectionStatusContextValue | undefined
>(undefined);

export function normalizeConnectionStatus(
  rawStatus?: string | null,
): string {
  const status = rawStatus?.toLowerCase() ?? "not_connected";
  if (status === "accepted" || status === "connected") return "connected";
  if (status === "pending") return "pending";
  if (status === "not_connected" || status === "not connected" || status === "connect") {
    return "not_connected";
  }
  return "not_connected";
}

export function isIncomingPendingConnection(
  state: ConnectionState | null | undefined,
): boolean {
  if (!state) return false;
  if (normalizeConnectionStatus(state.status) !== "pending") return false;
  const dir = (state.direction || "").toLowerCase();
  return dir === "incoming" || dir === "received";
}

function shouldSyncServerConnectionStatus(
  optimistic: ConnectionState | null | undefined,
  server: { status: string; direction?: string | null },
): boolean {
  if (!optimistic) return true;

  const optStatus = normalizeConnectionStatus(optimistic.status);
  const serverStatus = normalizeConnectionStatus(server.status);

  if (optStatus === serverStatus) {
    const optDir = optimistic.direction?.toLowerCase() ?? "";
    const serverDir = server.direction?.toLowerCase() ?? "";
    return optDir !== serverDir;
  }

  if (optStatus === "not_connected" && serverStatus !== "not_connected") {
    return true;
  }

  if (optStatus === "pending" && serverStatus === "connected") {
    return true;
  }

  return false;
}

export const ConnectionStatusProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [statusMap, setStatusMap] = useState<StatusMap>({});

  const getOptimisticStatus = useCallback(
    (userId: string) => statusMap[userId]?.status ?? null,
    [statusMap],
  );

  const getConnectionState = useCallback(
    (userId: string) => statusMap[userId] ?? null,
    [statusMap],
  );

  const setOptimisticStatus = useCallback(
    (userId: string, status: string | null, direction?: string) => {
      setStatusMap((prev) => {
        if (status === null) {
          const { [userId]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [userId]: {
            status: normalizeConnectionStatus(status),
            direction,
          },
        };
      });
    },
    [],
  );

  const syncFromServer = useCallback(
    (
      userId: string,
      status: string,
      direction?: string | null,
      force?: boolean,
    ) => {
      const normalizedStatus = normalizeConnectionStatus(status);
      setStatusMap((prev) => {
        const optimistic = prev[userId];
        const server = { status: normalizedStatus, direction };
        if (!force && !shouldSyncServerConnectionStatus(optimistic, server)) {
          return prev;
        }
        return {
          ...prev,
          [userId]: {
            status: normalizedStatus,
            direction: direction ?? undefined,
          },
        };
      });
    },
    [],
  );

  return (
    <ConnectionStatusContext.Provider
      value={{
        getOptimisticStatus,
        getConnectionState,
        setOptimisticStatus,
        syncFromServer,
      }}
    >
      {children}
    </ConnectionStatusContext.Provider>
  );
};

export const useConnectionStatus = () => {
  const ctx = useContext(ConnectionStatusContext);
  if (!ctx) {
    throw new Error(
      "useConnectionStatus must be used within a ConnectionStatusProvider",
    );
  }
  return ctx;
};
